import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Plus,
  Edit,
  Calendar,
  Camera,
  MapPin,
  Tag
} from 'lucide-react';
import { getAnimal, getAnimalEvents, getAnimalPhotos, getSmartKraalWeighings } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import WeightChart from '../../components/WeightChart';
import DataLoadError from '../../components/DataLoadError';
import EventTimeline from '../../components/EventTimeline';
import KraalTimeline from '../../components/KraalTimeline';
import MediaThumb from '../../components/MediaThumb';
import PresenceChip from '../../components/PresenceChip';
import StarButton from '../../components/StarButton';
import BindingChip, { isLiveBinding, registrationTabFor, eidForm, EID_FORM_LABEL } from '../../components/SmartKraalBindingChip';
import AssignEidModal from '../../components/AssignEidModal';
import { variantUrl } from '../../utils/media';
import { formatDay, presenceChip } from '../../utils/presence';
import { format, parseISO, differenceInMonths, differenceInYears } from 'date-fns';

// The lightbox (yet-another-react-lightbox) loads only when a photo is opened.
const MediaLightbox = lazy(() => import('../../components/MediaLightbox'));

// Calculate age from birth date
function calculateAge(birthDate) {
  if (!birthDate) return null;

  const birth = parseISO(birthDate);
  const now = new Date();
  const years = differenceInYears(now, birth);
  const months = differenceInMonths(now, birth) % 12;

  if (years > 0) {
    return `${years}y ${months}m`;
  }
  return `${months} months`;
}

// Format enum values for display (e.g., "born_on_farm" -> "Born on Farm")
function formatEnumValue(value) {
  if (!value) return null;
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export default function CattleDetail({ ranchId }) {
  const { id } = useParams();
  const { canWrite } = useAuth();
  const queryClient = useQueryClient();
  const [assigning, setAssigning] = useState(false);   // placeholder electronic id -> stick-read EID (4b-2)
  const [eidFlash, setEidFlash] = useState('');
  // One lightbox for the page (plan B1): the hero, the kraal timeline's stills
  // and clips, and the uploaded photos all open in it.
  const [lightbox, setLightbox] = useState({ open: false, slides: [], index: 0 });
  const openMedia = useCallback((slides, index = 0) => setLightbox({ open: true, slides, index }), []);
  const closeMedia = useCallback(() => setLightbox((l) => ({ ...l, open: false })), []);
  // Thumbnails that failed to load (variant not built yet): their slides open the original.
  const [heroPending, setHeroPending] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState({});
  // SmartKraal tag binding (mig 136) for the chip under the EID. Same query
  // the weighings section runs, so react-query fetches it once.
  const { data: sk } = useQuery({
    queryKey: ['smartkraalWeighings', id],
    queryFn: ({ signal }) => getSmartKraalWeighings(id, { signal }),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
  const tagLink = isLiveBinding(sk?.tag_link) ? sk.tag_link : null;   // a 'proposed' row is a suggestion, not a binding
  // the kind of electronic id: the server's verdict (from the binding's evidence) when
  // there is a binding, else the string shape
  const idForm = tagLink?.rfid_form || (sk?.tag_link?.rfid_form) || null;

  const { data: animal, isLoading, error } = useQuery({
    queryKey: ['animal', id],
    queryFn: () => getAnimal(id),
  });

  const { data: events = [] } = useQuery({
    queryKey: ['animalEvents', id],
    queryFn: () => getAnimalEvents(id),
    enabled: !!animal,
  });

  // Uploaded photos (the manual gallery; the kraal's own stills live on the
  // timeline). Join the same lightbox.
  const photosRanch = ranchId || animal?.ranch_id;
  const { data: photosPage } = useQuery({
    queryKey: ['animalPhotos', id, photosRanch],
    queryFn: () => getAnimalPhotos(photosRanch, { animal_id: id, limit: 100 }),
    enabled: !!animal && !!photosRanch,
    staleTime: 5 * 60 * 1000,
  });
  const photos = useMemo(() => (photosPage?.items || [])
    .filter((p) => p.photo_url && p.source !== 'smartkraal_crush'), [photosPage]);

  // Derive weight history from cattle events
  const weightHistory = useMemo(() => {
    if (!events || events.length === 0) return [];
    return events
      .filter(e => (e.event_type === 'weight' || e.event_type === 'weighing') && e.event_data?.weight_kg)
      .map(e => ({
        date: e.event_date,
        weight_kg: e.event_data.weight_kg,
        source: e.event_data.source || 'Manual',
      }));
  }, [events]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <DataLoadError error={error} title="Failed to load animal">
        <Link to="/cattle" className="btn-primary mt-4 inline-block">
          Back to Cattle List
        </Link>
      </DataLoadError>
    );
  }

  const age = calculateAge(animal.birth_date);
  const name = animal.local_name || animal.tag_id || 'Unnamed Animal';
  const hero = animal.hero;
  const presence = sk?.configured
    ? presenceChip({ smartkraal_p_idtag: tagLink?.p_idtag, last_seen: animal.last_seen, seen_days_ago: animal.seen_days_ago })
    : null;
  const openHero = () => hero && openMedia([{
    kind: 'image', src: heroPending ? hero.orig : hero.medium, orig: hero.orig, alt: name,
    caption: `${name}${hero.taken_on ? ` · ${formatDay(hero.taken_on)}` : ''}`,
    filename: `${name.replace(/[^\w-]+/g, '_')}_photo`,
  }], 0);
  const photoSlides = photos.map((p) => ({
    kind: 'image', src: pendingPhotos[p.id] ? p.photo_url : variantUrl(p.photo_url, 'medium'), orig: p.photo_url,
    alt: `${name}, ${p.photo_date || ''}`,
    caption: `${name}${p.photo_date ? ` · ${formatDay(p.photo_date)}` : ''}${p.notes ? ` · ${p.notes}` : ''}`,
    filename: `${name.replace(/[^\w-]+/g, '_')}_${p.photo_date || p.id}`,
  }));

  return (
    <div className="space-y-6">
      {eidFlash && (
        <div className="card p-3 text-sm border-primary-200 bg-primary-50" data-testid="eid-flash">{eidFlash}</div>
      )}
      {/* Header Bar */}
      <div className="flex items-center justify-between">
        <Link to="/cattle" className="inline-flex items-center text-earth-500 hover:text-earth-700">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Cattle
        </Link>
        {canWrite && (
          <div className="flex items-center gap-2">
            <Link to={`/cattle/${id}/edit`} className="btn bg-earth-100 text-earth-700 hover:bg-earth-200">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Link>
            <Link to={`/cattle/${id}/event`} className="btn btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Record Event
            </Link>
          </div>
        )}
      </div>

      {/* Animal Header Card */}
      <div className="card p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-4">
            {/* The display photo (hero; plan B1): tap to open it full size */}
            {hero ? (
              <button type="button" onClick={openHero} title="Open the photo"
                className="relative h-24 w-24 md:h-32 md:w-32 shrink-0 overflow-hidden rounded-xl bg-earth-100 ring-1 ring-earth-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                data-testid="hero-photo">
                {/* the 480 px thumb is sharp at this size on any phone (128 px x 3 = 384) */}
                <MediaThumb src={hero.thumb} alt={name} className="h-full w-full" onFail={() => setHeroPending(true)}
                            glyph={<span className="text-2xl font-bold text-primary-700">{name[0].toUpperCase()}</span>} />
              </button>
            ) : (
              <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-bold text-primary-700">
                  {(animal.local_name || animal.tag_id || 'A')[0].toUpperCase()}
                </span>
              </div>
            )}

            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-display font-bold text-primary-900">
                  {name}
                </h1>
                {/* the star (plan B2): kept on this device, listed by the wall's Starred filter */}
                <StarButton ranchId={ranchId || animal.ranch_id} animalId={animal.id} name={name} variant="inline" />
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-1 text-earth-600">
                <span className="flex items-center gap-1">
                  <Tag className="h-4 w-4" />
                  {animal.rfid_tag}
                </span>
                {(idForm || eidForm(animal.rfid_tag)) !== 'eid' && (
                  <span className="flex items-center gap-2">
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800" title="Written when the animal was registered from the crush record before the stick had read it">
                      {EID_FORM_LABEL[idForm || eidForm(animal.rfid_tag)]}
                    </span>
                    {canWrite && sk?.configured && (
                      <button type="button" className="text-xs text-primary-700 hover:underline" onClick={() => setAssigning(true)} data-testid="assign-eid-open">
                        Assign EID
                      </button>
                    )}
                  </span>
                )}
                {animal.tag_id && (
                  <span className="text-earth-400">•</span>
                )}
                {animal.tag_id && (
                  <span>Tag: {animal.tag_id}</span>
                )}
                {tagLink && (
                  <>
                    <span className="text-earth-400">•</span>
                    <BindingChip pIdtag={tagLink.p_idtag} status={tagLink.status} bindMethod={tagLink.bind_method}
                                 boundAt={tagLink.bound_at} to={registrationTabFor(tagLink.status)} testId="smartkraal-chip" />
                    {tagLink.status === 'sent_back' && tagLink.review_note && (
                      <span className="text-xs text-red-700" data-testid="smartkraal-review-note" title="The reviewer's note">
                        “{tagLink.review_note}”
                      </span>
                    )}
                  </>
                )}
                {presence && (
                  <>
                    <span className="text-earth-400">•</span>
                    <PresenceChip chip={presence} />
                  </>
                )}
                {sk?.configured && !tagLink && canWrite && animal.status === 'active' && (
                  <>
                    <span className="text-earth-400">•</span>
                    <Link to="/cattle/registration?tab=tagging" className="text-xs text-primary-700 hover:underline">
                      No SmartKraal tag · bind one
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <span className={`px-3 py-1 text-sm font-medium rounded-full capitalize ${
              animal.status === 'active'
                ? 'bg-green-100 text-green-800'
                : animal.status === 'sold'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-800'
            }`}>
              {animal.status}
            </span>
            <span className="px-3 py-1 text-sm font-medium rounded-full bg-primary-100 text-primary-800 capitalize">
              {animal.category}
            </span>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6 pt-6 border-t border-earth-200">
          <div>
            <div className="text-xs text-earth-500 uppercase tracking-wide">Sex</div>
            <div className="text-lg font-medium capitalize text-primary-900">{animal.sex}</div>
          </div>
          <div>
            <div className="text-xs text-earth-500 uppercase tracking-wide">Breed</div>
            <div className="text-lg font-medium text-primary-900">{animal.breed_name || '—'}</div>
          </div>
          <div>
            <div className="text-xs text-earth-500 uppercase tracking-wide">Age</div>
            <div className="text-lg font-medium text-primary-900">{age || '—'}</div>
          </div>
          <div>
            <div className="text-xs text-earth-500 uppercase tracking-wide">Current Weight</div>
            <div className="text-lg font-medium text-primary-900">
              {animal.last_weight_kg ? `${animal.last_weight_kg} kg` : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs text-earth-500 uppercase tracking-wide">Acquisition</div>
            <div className="text-lg font-medium text-primary-900">
              {formatEnumValue(animal.acquisition_type) || '—'}
            </div>
          </div>
        </div>

        {/* Additional Info */}
        {(animal.birth_date || animal.acquisition_date || animal.current_paddock_id) && (
          <div className="flex flex-wrap gap-4 mt-4 text-sm text-earth-600">
            {animal.birth_date && (
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Born: {format(parseISO(animal.birth_date), 'MMM d, yyyy')}
              </span>
            )}
            {animal.acquisition_date && (
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Acquired: {format(parseISO(animal.acquisition_date), 'MMM d, yyyy')}
              </span>
            )}
            {animal.current_paddock_name && (
              <span className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                Location: {animal.current_paddock_name}
              </span>
            )}
          </div>
        )}

        {/* Notes */}
        {animal.notes && (
          <div className="mt-4 p-3 bg-earth-50 rounded-lg text-sm text-earth-700">
            <span className="font-medium">Notes:</span> {animal.notes}
          </div>
        )}
      </div>

      {/* Two Column Layout for Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weight Chart */}
        <WeightChart
          weightHistory={weightHistory}
          currentWeight={animal.last_weight_kg}
        />

        {/* Quick Actions Card */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-primary-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <Link
              to={`/cattle/${id}/event?type=vaccination`}
              className="p-4 rounded-lg border border-earth-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-center"
            >
              <div className="text-2xl mb-1">💉</div>
              <div className="text-sm font-medium">Vaccination</div>
            </Link>
            <Link
              to={`/cattle/${id}/event?type=treatment`}
              className="p-4 rounded-lg border border-earth-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-center"
            >
              <div className="text-2xl mb-1">🩺</div>
              <div className="text-sm font-medium">Treatment</div>
            </Link>
            <Link
              to={`/cattle/${id}/event?type=observation`}
              className="p-4 rounded-lg border border-earth-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-center"
            >
              <div className="text-2xl mb-1">👁️</div>
              <div className="text-sm font-medium">Observation</div>
            </Link>
            <Link
              to={`/cattle/${id}/event?type=movement`}
              className="p-4 rounded-lg border border-earth-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-center"
            >
              <div className="text-2xl mb-1">🚚</div>
              <div className="text-sm font-medium">Movement</div>
            </Link>
          </div>
        </div>
      </div>

      {/* The animal's days at the kraal (mig 138; plan B1) — renders nothing when it has none */}
      <KraalTimeline animalId={id} animalName={name} onOpen={openMedia} />

      {/* Uploaded photos — the manual gallery, in the same lightbox */}
      {photos.length > 0 && (
        <div className="card p-6" data-testid="photo-gallery">
          <h3 className="text-lg font-medium text-primary-900 flex items-center gap-2 mb-4">
            <Camera className="h-5 w-5 text-purple-600" />
            Photos
            <span className="text-sm font-normal text-earth-500">({photos.length})</span>
          </h3>
          <div className="flex flex-wrap gap-2">
            {photos.map((p, i) => (
              <button key={p.id} type="button" onClick={() => openMedia(photoSlides, i)}
                title={p.photo_date ? formatDay(p.photo_date) : 'Open the photo'}
                className="relative h-24 w-32 overflow-hidden rounded-md bg-earth-100 ring-1 ring-earth-200 hover:ring-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500">
                <MediaThumb src={variantUrl(p.photo_url, 'thumb')} alt={`${name}, ${p.photo_date || ''}`} className="h-full w-full"
                            glyph={<Camera className="h-6 w-6 text-earth-400" aria-hidden="true" />}
                            onFail={() => setPendingPhotos((s) => ({ ...s, [p.id]: true }))} />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Event Timeline - Full Width */}
      <EventTimeline events={events} maxEvents={10} />
      {assigning && (
        <AssignEidModal ranchId={ranchId || animal.ranch_id} animal={{ ...animal, rfid_form: idForm }} onClose={() => setAssigning(false)}
                        onDone={(out) => {
                          setAssigning(false);
                          setEidFlash(out.changed ? `EID ${out.rfid_tag} assigned, replacing ${out.previous_rfid_tag}.` : `This animal already wears EID ${out.rfid_tag}.`);
                          queryClient.invalidateQueries({ queryKey: ['animal', id] });
                          queryClient.invalidateQueries({ queryKey: ['cattle'] });
                        }} />
      )}
      {lightbox.open && (
        <Suspense fallback={null}>
          <MediaLightbox open={lightbox.open} index={lightbox.index} slides={lightbox.slides} onClose={closeMedia}
                         shareUrl={typeof window !== 'undefined' ? window.location.href : undefined} shareTitle={name} />
        </Suspense>
      )}
    </div>
  );
}
